import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('click', this.#showSelectedVariantMedia, { signal });
    target?.addEventListener('pointerover', this.#warmFromOption, { signal });
    target?.addEventListener('focusin', this.#warmFromOption, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // lmt: other color photos are already in the gallery. Fetch them during idle time
    // so the first variant click does not wait on a download.
    const warmAll = () => this.#warmVariantMedia(target);
    if ('requestIdleCallback' in window) requestIdleCallback(warmAll, { timeout: 1200 });
    else setTimeout(warmAll, 250);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Clicking the variant that is already selected does not fire a change event.
   * Still move the gallery to that variant's photo.
   * @param {Event} event
   */
  #showSelectedVariantMedia = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const label = event.target.closest('label');
    const input =
      label?.querySelector('input[type="radio"][data-option-value-id]') ??
      event.target.closest('input[type="radio"][data-option-value-id], option[data-option-value-id]');
    if (!(input instanceof HTMLInputElement || input instanceof HTMLOptionElement)) return;
    const picker = input.closest('variant-picker');
    if (!picker) return;
    const mediaId = this.#mediaIdForSelection(picker, input);
    if (mediaId) this.#showMedia(mediaId);
  };

  /**
   * Handles a product select event.
   * The matching photo is already in the gallery, so show it immediately. The section
   * request still updates price and availability. Rebuild the gallery only when the
   * server response contains different media.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const mediaId = this.#mediaIdFromEvent(event);
    const shown = mediaId ? this.#showMedia(mediaId) : false;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        if (shown && this.#sameVisibleMedia(newMediaGallery)) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * @param {ProductSelectEvent} event
   * @returns {string}
   */
  #mediaIdFromEvent(event) {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (picker?.querySelector('[data-lmt-variant-media]')) return this.#mediaIdForSelection(picker);

    const optionValueId = event.detail?.optionValueId;
    if (!optionValueId || !(event.target instanceof Element)) return '';
    const option = event.target.querySelector(`[data-option-value-id="${CSS.escape(String(optionValueId))}"]`);
    return option instanceof HTMLElement ? option.dataset.mediaId || '' : '';
  }

  /**
   * Photo for the full selection (color and size together). A size button's own
   * media id is baked in at page load and still points at the previous color.
   *
   * @param {Element} picker
   * @param {HTMLInputElement | HTMLOptionElement | null} [override]
   * @returns {string}
   */
  #mediaIdForSelection(picker, override = null) {
    const script = picker.querySelector('[data-lmt-variant-media]');
    if (!script?.textContent) return '';

    let variants;
    try {
      variants = JSON.parse(script.textContent);
    } catch {
      return '';
    }
    if (!Array.isArray(variants)) return '';

    const selected = this.#selectedOptionValues(picker, override);
    const groups = [...picker.querySelectorAll('form > .variant-option')];
    const colorIndex = groups.findIndex((group) => group.classList.contains('lmt-color-option'));
    if (colorIndex >= 0 && !selected[colorIndex]) return '';

    const matches = (variant, partial) =>
      Array.isArray(variant?.options) &&
      variant.options.length === selected.length &&
      variant.options.every((option, index) => (partial ? !selected[index] || option === selected[index] : option === selected[index]));
    const match = variants.find((variant) => matches(variant, false)) || variants.find((variant) => matches(variant, true));
    if (match?.media == null || match.media === '') return '';
    return String(match.media);
  }

  /**
   * @param {Element} picker
   * @param {HTMLInputElement | HTMLOptionElement | null} [override]
   * @returns {string[]}
   */
  #selectedOptionValues(picker, override = null) {
    /** @type {string[]} */
    const values = [];
    for (const group of picker.querySelectorAll('form > .variant-option')) {
      const select = group.querySelector('select');
      if (select instanceof HTMLSelectElement) {
        const chosen = override instanceof HTMLOptionElement && select.contains(override) ? override.value : select.value;
        values.push(chosen);
        continue;
      }

      const radios = [...group.querySelectorAll('input[type="radio"]')];
      if (radios.length) {
        const chosen =
          override instanceof HTMLInputElement && radios.includes(override)
            ? override.value
            : radios.find((radio) => radio instanceof HTMLInputElement && radio.checked);
        values.push(chosen instanceof HTMLInputElement ? chosen.value : chosen || '');
        continue;
      }

      if (group instanceof HTMLElement && group.dataset.optionValue) values.push(group.dataset.optionValue);
    }
    return values;
  }

  /**
   * @param {Event} event
   */
  #warmFromOption = (event) => {
    if (!(event.target instanceof Element)) return;
    const label = event.target.closest('label');
    const option =
      label?.querySelector('input[data-option-value-id]') ??
      event.target.closest('input[data-option-value-id], option[data-option-value-id]');
    if (!(option instanceof HTMLInputElement || option instanceof HTMLOptionElement)) return;
    const picker = option.closest('variant-picker');
    if (!picker) return;
    const mediaId = picker.querySelector('[data-lmt-variant-media]')
      ? this.#mediaIdForSelection(picker, option)
      : option.dataset.mediaId;
    if (mediaId) this.#warmMedia(mediaId);
  };

  /**
   * @param {Element | null | undefined} root
   */
  #warmVariantMedia(root) {
    const picker = root?.querySelector('variant-picker');
    const script = picker?.querySelector('[data-lmt-variant-media]');
    if (script?.textContent) {
      try {
        const variants = JSON.parse(script.textContent);
        if (Array.isArray(variants)) {
          for (const mediaId of new Set(variants.map((variant) => (variant?.media == null ? '' : String(variant.media))))) {
            if (mediaId) this.#warmMedia(mediaId, 'low');
          }
          return;
        }
      } catch {
        // Fall through to the option attributes.
      }
    }

    const ids = new Set(
      [...(root?.querySelectorAll('[data-option-value-id][data-media-id]') ?? [])].map((option) =>
        option instanceof HTMLElement ? option.dataset.mediaId : ''
      )
    );
    for (const mediaId of ids) {
      if (mediaId) this.#warmMedia(mediaId, 'low');
    }
  }

  /**
   * @param {string} mediaId
   * @param {'high' | 'low'} [priority]
   */
  #warmMedia(mediaId, priority = 'high') {
    const slide = this.#slideForMedia(mediaId);
    const image = slide?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;
    if (priority === 'low' && slide?.getAttribute('aria-hidden') === 'false') return;
    if (image.dataset.lmtWarm === 'high' || image.dataset.lmtWarm === priority) return;
    image.dataset.lmtWarm = priority;
    image.loading = 'eager';
    if (image.fetchPriority !== 'high') image.fetchPriority = priority;
    image.decode?.().catch(() => {});
  }

  /**
   * @param {string} mediaId
   * @returns {HTMLElement | null}
   */
  #slideForMedia(mediaId) {
    const slide = this.querySelector(`slideshow-slide:has([data-media-id="${CSS.escape(mediaId)}"])`);
    if (slide instanceof HTMLElement) return slide;
    const gridItem = this.querySelector(`.media-gallery__grid [data-media-id="${CSS.escape(mediaId)}"]`);
    return gridItem instanceof HTMLElement ? gridItem : null;
  }

  /**
   * @param {string} mediaId
   * @returns {boolean}
   */
  #showMedia(mediaId) {
    const target = this.#slideForMedia(mediaId);
    if (!target) return false;
    this.#warmMedia(mediaId);

    const slideshow = this.slideshow;
    if (slideshow && target.matches('slideshow-slide')) {
      const slides = slideshow.refs.slides ?? [];
      const index = slides.indexOf(target);
      if (index < 0) return false;
      if (slideshow.current !== index) slideshow.select(index, undefined, { animate: false });
      return slideshow.current === index;
    }

    target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return true;
  }

  /**
   * @param {Element} newGallery
   * @returns {boolean}
   */
  #sameVisibleMedia(newGallery) {
    const ids = (root) =>
        [...root.querySelectorAll('slideshow-slide .product-media[data-media-id], .media-gallery__grid .product-media[data-media-id]')]
        .map((media) => (media instanceof HTMLElement ? media.dataset.mediaId : ''))
        .filter(Boolean)
        .sort()
        .join(',');
    const current = ids(this);
    return current !== '' && current === ids(newGallery);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
